/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IContentBlockViewProps } from '@msdyn365-commerce-modules/content-block';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { Image, IViewPort } from '@msdyn365-commerce/core';
import { getFeatureToggleService } from '../../../services/featureToggleService';
import { IContentBlockConfig, IContentBlockProps } from '../definition-extensions/content-block.ext.props.autogenerated';

const DEFAULT_IMAGE_MODE = 2;

const DEFAULT_VIEWPORTS: IViewPort = {
    xs: { w: 800, h: 600 }, // max-width: 767px
    sm: { w: 1200, h: 900 }, // min-width: 768px and max-width: 991px
    md: { w: 1600, h: 900 }, // min-width: 992px and max-width: 1199px
    lg: { w: 1600, h: 700 }, // min-width: 1200px and max-width: 1599px
    xl: { w: 1600, h: 700 } // min-width: 1600px
};

type IContentBlockFullConfig = IContentBlockConfig & {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- This field comes from SDK.
    msdyn365__moduleLayout?: string;
};

const ContentBlockView: React.FC<IContentBlockViewProps & IContentBlockProps<{}>> = props => {
    const { contentBlockContainer, imageContainer, detailsContainer, title, text, links, image: imageProp } = props;
    const { image } = props.config;
    const imageProps = {
        gridSettings: props.context.request.gridSettings ?? {},
        imageSettings: {
            ...(image?.imageSettings ?? {}),
            mode: image?.imageSettings?.mode || DEFAULT_IMAGE_MODE,
            viewports: image?.imageSettings?.viewports || DEFAULT_VIEWPORTS // Use || instead of ?? here
        }
    };

    const isLcpImage = props.config.preload;

    const htmlImage = imageProp && (
        <img // @ts-ignore
            src={imageProp.props.src} // @ts-ignore
            alt={imageProp.props.altText} // @ts-ignore
            title={imageProp.props.title} // @ts-ignore
            fetchpriority={isLcpImage ? 'high' : undefined}
            className={isLcpImage ? 'lcp-image' : undefined}
        />
    );

    //@ts-ignore
    const msdynImage = (
        <Image
            {...image}
            {...imageProps}
            requestContext={props.context.request}
            editProps={{
                key: image ?? {},
                requestContext: props.context.request,
                moduleType: props.typeName,
                imagePropertyName: 'image',
                moduleId: props.id,
                layout: (props.config as IContentBlockFullConfig).msdyn365__moduleLayout
            }}
            preserveImageSpace
            shouldSkipToMainImage
            fetchpriority={isLcpImage ? 'high' : undefined}
            className={isLcpImage ? 'lcp-image' : undefined}
        />
    );
    const { isFeatureEnabled } = getFeatureToggleService(props.context.request.cookies, props.context.app.config.features);

    const contentBlockImage = isFeatureEnabled('msdyn-image') ? msdynImage : htmlImage;

    const entireLink = contentBlockContainer.className.includes('linkplacement__entirebanner');
    // @ts-ignore
    const link = links && 'props' in links ? links.props.links[0].linkUrl.destinationUrl : '';
    return (
        <Module {...contentBlockContainer}>
            {entireLink ? (
                <a href={link}>
                    <Node {...imageContainer}>{contentBlockImage}</Node>
                    <Node {...detailsContainer}>
                        {title}
                        {text}
                    </Node>
                </a>
            ) : (
                <>
                    {image && <Node {...imageContainer}>{contentBlockImage}</Node>}
                    <Node {...detailsContainer}>
                        {title}
                        {text}
                        {links}
                    </Node>
                </>
            )}
        </Module>
    );
};

export default ContentBlockView;
